<template>
  <div class="container">
    <div class="dropdown-container">
      <!-- Distributor -->
      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributor" :disabled="isDistributorLocked">
          <option value="">Select Distributor</option>
          <option v-if="!isDistributorUser" :value="ALL">All Distributors</option>

          <option v-for="distributor in distributorList" :key="distributor.profile_id"
            :value="String(distributor.profile_id)">
            {{ distributor.name }}
          </option>
        </select>
      </div>

      <!-- Date (single) -->
      <div class="dropdown-item">
        <label>Date</label>
        <input type="date" v-model="selectedDate" :max="currentDate" @change="handleDateChange"
          @blur="validateSelectedDate(true)" />
      </div>

      <!-- Search -->
      <div class="dropdown-item search-container">
        <button @click="performSearch" class="btn button-default mt-4" :disabled="loading">
          {{ loading ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Export -->
      <div class="dropdown-item search-container">
        <button @click="exportToExcel" class="btn button-export mt-4" :disabled="!reportData.length">
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- ✅ Table -->
    <div v-if="!loading && reportData.length && (itemsLoaded.length || itemsSold.length || itemsBal.length)"
      class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <!-- Header row 1 -->
            <tr class="header-row-1">
              <th rowspan="2">FSM</th>
              <th rowspan="2">ASM</th>
              <th rowspan="2">Distributor</th>
              <th rowspan="2">Vehicle No</th>

              <th :colspan="itemsLoaded.length + 1" class="text-center group-end">Loaded Qty</th>
              <th :colspan="itemsSold.length + 1" class="text-center group-end">Sold Qty</th>
              <th :colspan="itemsBal.length + 1" class="text-center">Balance Qty</th>
            </tr>

            <!-- Header row 2 -->
            <tr class="header-row-2">
              <!-- Loaded -->
              <th v-for="it in itemsLoaded" :key="'l' + it.item_id" class="prod-head">
                <div class="prod-head-lines">
                  <div v-for="(w, idx) in headerWords(it.item_name)" :key="'lw' + it.item_id + '_' + idx">
                    {{ w }}
                  </div>
                </div>
              </th>
              <th class="text-center section-end">Total</th>

              <!-- Sold -->
              <th v-for="(it, idx) in itemsSold" :key="'s' + it.item_id"
                :class="['prod-head', idx === 0 ? 'section-start' : '']">
                <div class="prod-head-lines">
                  <div v-for="(w, idx2) in headerWords(it.item_name)" :key="'sw' + it.item_id + '_' + idx2">
                    {{ w }}
                  </div>
                </div>
              </th>
              <th class="text-center section-end">Total</th>

              <!-- Balance -->
              <th v-for="(it, idx) in itemsBal" :key="'b' + it.item_id"
                :class="['prod-head', idx === 0 ? 'section-start' : '']">
                <div class="prod-head-lines">
                  <div v-for="(w, idx2) in headerWords(it.item_name)" :key="'bw' + it.item_id + '_' + idx2">
                    {{ w }}
                  </div>
                </div>
              </th>
              <th class="text-center">Total</th>
            </tr>
          </thead>

          <tbody>
            <!-- ✅ REAL REPORT ROWS (unchanged) -->
            <tr v-for="(row, i) in reportData" :key="row.dispatch_id ?? i">
              <td>{{ row.fsm_name || "-" }}</td>
              <td>{{ row.asm_name || "-" }}</td>
              <td>{{ row.distributor_name || "-" }}</td>
              <td>{{ vehicleNoOf(row.rep_id) || row.vehicle_no || "-" }}</td>

              <!-- Loaded -->
              <td v-for="it in itemsLoaded" :key="'lv' + it.item_id" class="text-end">
                {{ formatQty(row?.["loaded_" + it.item_id] ?? 0) }}
              </td>
              <td class="text-end section-end">
                <strong>{{ formatQty(row.loaded_total ?? 0) }}</strong>
              </td>

              <!-- Sold -->
              <td v-for="(it, idx) in itemsSold" :key="'sv' + it.item_id"
                :class="['text-end', idx === 0 ? 'section-start' : '']">
                {{ formatQty(row?.["sold_" + it.item_id] ?? 0) }}
              </td>
              <td class="text-end section-end">
                <strong>{{ formatQty(row.sold_total ?? 0) }}</strong>
              </td>

              <!-- Balance -->
              <td v-for="(it, idx) in itemsBal" :key="'bv' + it.item_id"
                :class="['text-end', idx === 0 ? 'section-start' : '']">
                {{ formatQty(row?.["bal_" + it.item_id] ?? 0) }}
              </td>
              <td class="text-end">
                <strong>{{ formatQty(row.bal_total ?? 0) }}</strong>
              </td>
            </tr>

            <!-- ✅ totals row (ONLY REAL reportData) -->
            <tr v-if="reportData.length" class="total-row">
              <td colspan="4" class="text-end"><strong>Total</strong></td>

              <!-- Loaded totals -->
              <td v-for="it in itemsLoaded" :key="'lt' + it.item_id" class="text-end">
                <strong>{{ formatQty(columnTotal("loaded_", it.item_id)) }}</strong>
              </td>
              <td class="text-end"><strong>{{ formatQty(sumField("loaded_total")) }}</strong></td>

              <!-- Sold totals -->
              <td v-for="it in itemsSold" :key="'st' + it.item_id" class="text-end">
                <strong>{{ formatQty(columnTotal("sold_", it.item_id)) }}</strong>
              </td>
              <td class="text-end"><strong>{{ formatQty(sumField("sold_total")) }}</strong></td>

              <!-- Balance totals -->
              <td v-for="it in itemsBal" :key="'bt' + it.item_id" class="text-end">
                <strong>{{ formatQty(columnTotal("bal_", it.item_id)) }}</strong>
              </td>
              <td class="text-end"><strong>{{ formatQty(sumField("bal_total")) }}</strong></td>
            </tr>

            <!-- ✅ Section title row -->
            <tr v-if="showNoDispatchRows" class="no-dispatch-title">
              <td :colspan="tableColspan">
                <strong>No Dispatch Distributors ({{ noDispatchRows.length }})</strong>
              </td>
            </tr>

            <!-- ✅ No-dispatch distributor rows (all zeros, vehicle blank) -->
            <tr v-for="(row, k) in noDispatchRows" :key="'nd_' + (row.distributor_profile_id || k)"
              class="no-dispatch-row">
              <td>{{ row.fsm_name || "-" }}</td>
              <td>{{ row.asm_name || "-" }}</td>
              <td>{{ row.distributor_name || "-" }}</td>
              <td><!-- ✅ must be blank --></td>

              <!-- Loaded (0) -->
              <td v-for="it in itemsLoaded" :key="'ndl' + it.item_id + '_' + k" class="text-end">
                {{ formatQty(row?.["loaded_" + it.item_id] ?? 0) }}
              </td>
              <td class="text-end section-end">
                <strong>{{ formatQty(row.loaded_total ?? 0) }}</strong>
              </td>

              <!-- Sold (0) -->
              <td v-for="(it, idx) in itemsSold" :key="'nds' + it.item_id + '_' + k"
                :class="['text-end', idx === 0 ? 'section-start' : '']">
                {{ formatQty(row?.["sold_" + it.item_id] ?? 0) }}
              </td>
              <td class="text-end section-end">
                <strong>{{ formatQty(row.sold_total ?? 0) }}</strong>
              </td>

              <!-- Balance (0) -->
              <td v-for="(it, idx) in itemsBal" :key="'ndb' + it.item_id + '_' + k"
                :class="['text-end', idx === 0 ? 'section-start' : '']">
                {{ formatQty(row?.["bal_" + it.item_id] ?? 0) }}
              </td>
              <td class="text-end">
                <strong>{{ formatQty(row.bal_total ?? 0) }}</strong>
              </td>
            </tr>

          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!loading && !reportData.length" class="no-data-message">
      <p>No data found. Please select filters and click Search.</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  getDistributorList,
  fetchDailySalesMonitoringReport,
  fetchVehicleListrep,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: "DailySalesMonitoringForm",

  data() {
    const ALL = "__all__";
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: "",

      distributorList: [],
      selectedDistributor: "",
      isDistributorLocked: false,

      selectedDate: currentDate,
      currentDate,

      loading: false,

      itemsLoaded: [],
      itemsSold: [],
      itemsBal: [],
      reportData: [],



      vehicles: [],
      vehicleMap: {},
      distChainMap: {},


      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    // full columns count for colspan rows
    tableColspan() {
      return (
        4 +
        (this.itemsLoaded.length + 1) +
        (this.itemsSold.length + 1) +
        (this.itemsBal.length + 1)
      );
    },

    // ✅ build no-dispatch rows (table rows)
    // noDispatchRows() {
    //   // show only when All Distributors (and not distributor user)
    //   if (this.isDistributorUser) return [];
    //   if (this.selectedDistributor !== this.ALL) return [];

    //   const list = Array.isArray(this.distributorList) ? this.distributorList : [];
    //   const rows = Array.isArray(this.reportData) ? this.reportData : [];

    //   const dispatchedSet = new Set(
    //     rows
    //       .map((r) => Number(r?.distributor_profile_id || 0))
    //       .filter(Boolean)
    //   );

    //   // safety: if backend not sending distributor_profile_id, don't show wrong results
    //   if (rows.length > 0 && dispatchedSet.size === 0) return [];

    //   const nameOf = (d) => {
    //     return (
    //       d?.name ??
    //       d?.business_name ??
    //       d?.full_name ??
    //       [d?.first_name, d?.last_name].filter(Boolean).join(" ") ??
    //       String(d?.profile_id ?? "")
    //     );
    //   };

    //   // helper: best-effort fsm/asm if API gives (otherwise '-')
    //   const fsmOf = (d) => d?.fsm_name ?? d?.fsm ?? d?.fsm_user_name ?? "";
    //   const asmOf = (d) => d?.asm_name ?? d?.asm ?? d?.asm_user_name ?? "";

    //   // create row objects with all totals 0 and cells missing => render as 0
    //   return list
    //     .filter((d) => !dispatchedSet.has(Number(d?.profile_id || 0)))
    //     .map((d) => {
    //       const out = {
    //         is_no_dispatch: true,
    //         distributor_profile_id: Number(d?.profile_id || 0),

    //         fsm_name: fsmOf(d),
    //         asm_name: asmOf(d),
    //         distributor_name: nameOf(d),

    //         rep_id: null,
    //         vehicle_no: "",

    //         loaded_total: 0,
    //         sold_total: 0,
    //         bal_total: 0,
    //       };

    //       // set visible item columns as 0 (optional but good for excel/export and clarity)
    //       this.itemsLoaded.forEach((it) => (out["loaded_" + it.item_id] = 0));
    //       this.itemsSold.forEach((it) => (out["sold_" + it.item_id] = 0));
    //       this.itemsBal.forEach((it) => (out["bal_" + it.item_id] = 0));

    //       return out;
    //     });
    // },

    //     noDispatchRows() {
    //   if (this.isDistributorUser) return [];
    //   if (this.selectedDistributor !== this.ALL) return [];

    //   const list = Array.isArray(this.distributorList) ? this.distributorList : [];
    //   const rows = Array.isArray(this.reportData) ? this.reportData : [];

    //   const dispatchedSet = new Set(
    //     rows.map((r) => Number(r?.distributor_profile_id || 0)).filter(Boolean)
    //   );

    //   if (rows.length > 0 && dispatchedSet.size === 0) return [];

    //   const nameOf = (d) =>
    //     String(
    //       d?.name ??
    //       d?.business_name ??
    //       d?.full_name ??
    //       [d?.first_name, d?.last_name].filter(Boolean).join(" ") ??
    //       d?.profile_id ??
    //       d?.id ??
    //       ""
    //     ).trim();

    //   const chainMap = this.distChainMap || {};

    //   return list
    //     .filter((d) => !dispatchedSet.has(Number(d?.profile_id || d?.id || 0)))
    //     .map((d) => {
    //       const distId = Number(d?.profile_id || d?.id || 0);
    //       const chain = chainMap[distId] || {};

    //       const fsm = String(chain?.fsm_name ?? d?.fsm_name ?? "").trim();
    //       const asm = String(chain?.asm_name ?? d?.asm_name ?? "").trim();

    //       const out = {
    //         is_no_dispatch: true,
    //         distributor_profile_id: distId,

    //         fsm_name: fsm,
    //         asm_name: asm,
    //         distributor_name: nameOf(d),

    //         rep_id: null,
    //         vehicle_no: "",

    //         loaded_total: 0,
    //         sold_total: 0,
    //         bal_total: 0,
    //       };

    //       this.itemsLoaded.forEach((it) => (out["loaded_" + it.item_id] = 0));
    //       this.itemsSold.forEach((it) => (out["sold_" + it.item_id] = 0));
    //       this.itemsBal.forEach((it) => (out["bal_" + it.item_id] = 0));

    //       return out;
    //     });
    // },

    noDispatchRows() {
      if (this.isDistributorUser) return [];
      if (this.selectedDistributor !== this.ALL) return [];

      const list = Array.isArray(this.distributorList) ? this.distributorList : [];
      const rows = Array.isArray(this.reportData) ? this.reportData : [];

      const dispatchedSet = new Set(
        rows.map((r) => Number(r?.distributor_profile_id || 0)).filter(Boolean)
      );

      if (rows.length > 0 && dispatchedSet.size === 0) return [];

      const nameOf = (d) =>
        String(
          d?.name ??
          d?.business_name ??
          d?.full_name ??
          [d?.first_name, d?.last_name].filter(Boolean).join(" ") ??
          d?.profile_id ??
          d?.id ??
          ""
        ).trim();

      const chainMap = this.distChainMap || {};

      return list
        .filter((d) => !dispatchedSet.has(Number(d?.profile_id || d?.id || 0)))
        .map((d) => {
          const distId = Number(d?.profile_id || d?.id || 0);
          const chain = chainMap[distId] || {};

          const fsm = String(chain?.fsm_name ?? d?.fsm_name ?? "").trim();
          const asm = String(chain?.asm_name ?? d?.asm_name ?? "").trim();

          const out = {
            is_no_dispatch: true,
            distributor_profile_id: distId,

            fsm_name: fsm,
            asm_name: asm,
            distributor_name: nameOf(d),

            rep_id: null,
            vehicle_no: "",

            loaded_total: 0,
            sold_total: 0,
            bal_total: 0,
          };

          this.itemsLoaded.forEach((it) => (out["loaded_" + it.item_id] = 0));
          this.itemsSold.forEach((it) => (out["sold_" + it.item_id] = 0));
          this.itemsBal.forEach((it) => (out["bal_" + it.item_id] = 0));

          return out;
        })
        // ✅ SORT: FSM name A-Z (blank FSM goes last)
        .sort((a, b) => {
          const fa = String(a?.fsm_name ?? "").trim().toLowerCase();
          const fb = String(b?.fsm_name ?? "").trim().toLowerCase();

          if (!fa && !fb) return 0;
          if (!fa) return 1; // blank last
          if (!fb) return -1;

          // alphabetical
          if (fa < fb) return -1;
          if (fa > fb) return 1;

          // tie-breaker: ASM then Distributor
          const aa = String(a?.asm_name ?? "").trim().toLowerCase();
          const ab = String(b?.asm_name ?? "").trim().toLowerCase();
          if (aa < ab) return -1;
          if (aa > ab) return 1;

          const da = String(a?.distributor_name ?? "").trim().toLowerCase();
          const db = String(b?.distributor_name ?? "").trim().toLowerCase();
          if (da < db) return -1;
          if (da > db) return 1;

          return 0;
        });
    },




    showNoDispatchRows() {
      return this.reportData.length > 0 && this.noDispatchRows.length > 0;
    },
  },

  mounted() {
    this.initLoginAndLoad();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    bodyOf(x) {
      return x?.data ?? x;
    },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },

    headerWords(name) {
      return String(name || "").trim().split(/\s+/).filter(Boolean);
    },

    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();
      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      if (http === 401) return authMsg.test(msg);
      return http === 419 || http === 440 || http === 498 || authMsg.test(msg);
    },

    redirectToLogin(message) {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", message || "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },

    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },

    // ✅ active user filter
    isActiveUser(x) {
      const v = x?.is_active ?? x?.user_is_active ?? x?.app_user_is_active ?? x?.active;
      if (v === undefined || v === null) return true;
      return v === true || Number(v) === 1;
    },
    onlyActive(list) {
      return (Array.isArray(list) ? list : []).filter(this.isActiveUser);
    },

    async initLoginAndLoad() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token_set") || "";
        if (!token) return this.redirectToLogin("Session expired. Please login again.");
        this.token = token;

        this.myProfileId = Number(localStorage.getItem("profile_id") || 0);

        const labelFromStorage =
          localStorage.getItem("user_label") ||
          localStorage.getItem("userLabel") ||
          localStorage.getItem("role") ||
          "";

        this.userLabel = String(labelFromStorage);
        const labelUpper = String(this.userLabel || "").toUpperCase().trim();

        if (
          labelUpper === "DISTRIBUTOR" ||
          labelUpper.startsWith("DISTRIBUTOR") ||
          labelUpper === "DIS" ||
          labelUpper.startsWith("DIS_")
        ) {
          this.roleKey = "DIS";
        } else {
          this.roleKey = "OTHER";
        }

        await this.loadDistributorListOnly();
        if (this.redirecting) return;

        await this.loadVehiclesByRep();

        this.itemsLoaded = [];
        this.itemsSold = [];
        this.itemsBal = [];
        this.reportData = [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to initialize page.");
      } finally {
        this.loading = false;
      }
    },

    async loadVehiclesByRep() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchVehicleListrep(profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
        this.vehicles = list;

        const m = {};
        for (const v of list) {
          const repId = Number(
            v?.rep_id ?? v?.sales_rep_id ?? v?.profile_id ?? v?.rep_profile_id ?? 0
          );

          const no = String(
            v?.vehicle_no ??
            v?.vehicle_number ??
            v?.registration_no ??
            v?.reg_no ??
            v?.vehicle ??
            v?.first_name ??
            ""
          );

          if (repId) m[repId] = no || "-";
        }
        this.vehicleMap = m;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
      }
    },

    vehicleNoOf(repId) {
      const id = Number(repId || 0);
      if (!id) return "";
      return this.vehicleMap[id] || "";
    },

    async loadDistributorListOnly() {
      try {
        const profileId = localStorage.getItem("profile_id");

        const res = await getDistributorList(profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        if (this.handleUnauthorized(body)) return;

        let list = this.onlyActive(body?.data || []);

        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find((d) => Number(d.profile_id) === Number(this.myProfileId));
          if (mine) {
            list = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;
          }
        }

        this.distributorList = list;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to load Distributor list.");
      }
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;
      if (dateStr.includes("/")) return false;
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;
      const y = Number(m[1]),
        mo = Number(m[2]),
        d = Number(m[3]);
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateSelectedDate(showToast = true) {
      if (!this.selectedDate) {
        if (showToast) this.toast("error", "Please select Date");
        return false;
      }
      if (!this.isValidISODate(this.selectedDate)) {
        if (showToast) this.toast("error", "Invalid Date");
        this.selectedDate = "";
        return false;
      }
      if (this.selectedDate > this.currentDate) {
        if (showToast) this.toast("error", "Date cannot be in the future");
        this.selectedDate = "";
        return false;
      }
      return true;
    },

    handleDateChange() {
      this.validateSelectedDate(false);
    },

    validateFilters() {
      if (!this.selectedDistributor) {
        this.toast("error", "Please select Distributor.");
        return false;
      }
      return this.validateSelectedDate(true);
    },

    formatQty(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },

    columnTotal(prefix, itemId) {
      const key = prefix + String(itemId);
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[key]) || 0), 0);
    },

    sumField(field) {
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[field]) || 0), 0);
    },

    async performSearch() {
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFilters()) return;

      this.loading = true;
      this.itemsLoaded = [];
      this.itemsSold = [];
      this.itemsBal = [];
      this.reportData = [];

      const toQty = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, "").trim());
        return Number.isFinite(n) ? n : 0;
      };

      const uiIsZero = (n) => Math.round(toQty(n)) === 0;

      const readQty = (row, prefix, id) => {
        const base = `${prefix}${id}`;
        if (row && row[base] !== undefined) return toQty(row[base]);

        if (row) {
          const k = Object.keys(row).find((x) => x.startsWith(base));
          if (k) return toQty(row[k]);
        }
        return 0;
      };

      try {
        const payload = {
          start_date: this.selectedDate,
          end_date: this.selectedDate,
          distributor_profile_id:
            this.selectedDistributor === this.ALL ? null : this.selectedDistributor,
        };

        const apiRes = await fetchDailySalesMonitoringReport(this.token, payload);
        if (this.handleUnauthorized(apiRes)) return;

        const res = this.bodyOf(apiRes);
        if (this.handleUnauthorized(res)) return;

        if (!res || res.status !== true) {
          this.toast("error", res?.message || "Error loading report.");
          return;
        }

        // ✅ build distributor -> ASM/FSM map from backend response
        this.distChainMap = {};
        const chain = Array.isArray(res.distributor_chain) ? res.distributor_chain : [];
        for (const c of chain) {
          const id = Number(c?.distributor_profile_id || 0);
          if (!id) continue;

          this.distChainMap[id] = {
            fsm_name: String(c?.fsm_name ?? "").trim(),
            asm_name: String(c?.asm_name ?? "").trim(),
          };
        }


        const rows = Array.isArray(res.data) ? res.data : [];
        if (!rows.length) {
          this.toast("error", res?.message || "No data found for selected filters.");
          return;
        }

        const rawLoaded = Array.isArray(res.items_loaded) ? res.items_loaded : [];
        const rawSold = Array.isArray(res.items_sold) ? res.items_sold : [];
        const rawBal = Array.isArray(res.items_bal) ? res.items_bal : [];

        const itemsLoadedAll = rawLoaded
          .map((x) => ({
            item_id: String(x.item_id),
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          }))
          .sort((a, b) => a.sort_id - b.sort_id);

        const itemsSoldAll = rawSold
          .map((x) => ({
            item_id: String(x.item_id),
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          }))
          .sort((a, b) => a.sort_id - b.sort_id);

        const itemsBalAll = rawBal
          .map((x) => ({
            item_id: String(x.item_id),
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          }))
          .sort((a, b) => a.sort_id - b.sort_id);

        const mappedRows = rows.map((r) => {
          const out = { ...r };

          itemsLoadedAll.forEach((it) => {
            out["loaded_" + it.item_id] = readQty(r, "loaded_", it.item_id);
          });

          itemsSoldAll.forEach((it) => {
            out["sold_" + it.item_id] = readQty(r, "sold_", it.item_id);
          });

          itemsBalAll.forEach((it) => {
            out["bal_" + it.item_id] = readQty(r, "bal_", it.item_id);
          });

          out.loaded_total = toQty(r?.loaded_total);
          out.sold_total = toQty(r?.sold_total);
          out.bal_total = toQty(r?.bal_total);
          out.rep_id = r?.rep_id ?? null;

          return out;
        });

        const totalsLoaded = {};
        const totalsSold = {};
        const totalsBal = {};

        itemsLoadedAll.forEach((it) => (totalsLoaded[it.item_id] = 0));
        itemsSoldAll.forEach((it) => (totalsSold[it.item_id] = 0));
        itemsBalAll.forEach((it) => (totalsBal[it.item_id] = 0));

        mappedRows.forEach((r) => {
          itemsLoadedAll.forEach((it) => {
            totalsLoaded[it.item_id] += toQty(r["loaded_" + it.item_id]);
          });
          itemsSoldAll.forEach((it) => {
            totalsSold[it.item_id] += toQty(r["sold_" + it.item_id]);
          });
          itemsBalAll.forEach((it) => {
            totalsBal[it.item_id] += toQty(r["bal_" + it.item_id]);
          });
        });

        this.itemsLoaded = itemsLoadedAll.filter((it) => !uiIsZero(totalsLoaded[it.item_id]));
        this.itemsSold = itemsSoldAll.filter((it) => !uiIsZero(totalsSold[it.item_id]));
        this.itemsBal = itemsBalAll.filter((it) => !uiIsZero(totalsBal[it.item_id]));

        if (!this.itemsLoaded.length && !this.itemsSold.length && !this.itemsBal.length) {
          this.toast("error", "All product totals are 0 for this date.");
          return;
        }

        this.reportData = mappedRows; // ✅ only real report rows
        this.toast("success", "Report loaded successfully.");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", err?.response?.data?.message || "Error loading report.");
      } finally {
        this.loading = false;
      }
    },

    // ✅ Excel export includes "no dispatch rows" AFTER total (without affecting totals)
    exportToExcel() {
      if (!this.reportData.length) {
        this.toast("error", "No data to export.");
        return;
      }

      const baseCols = ["FSM", "ASM", "Distributor", "Vehicle No"];

      // ---------------- FILTERS ----------------
      const filterRows = [
        ["Daily Sales Monitoring Report"],
        [],
        ["Distributor", this.selectedDistributor === this.ALL ? "All" : this.distributorList.find(d => String(d.profile_id) === String(this.selectedDistributor))?.name || "-"],
        ["Date", this.selectedDate],
        [],
      ];

      // ---------------- HEADERS ----------------
      const loadedNames = this.itemsLoaded.map((it) => it.item_name);
      const soldNames = this.itemsSold.map((it) => it.item_name);
      const balNames = this.itemsBal.map((it) => it.item_name);

      const loadedLen = this.itemsLoaded.length + 1; // +Total
      const soldLen = this.itemsSold.length + 1;
      const balLen = this.itemsBal.length + 1;

      const headerRow1 = [
        ...baseCols,
        "Loaded Qty",
        ...Array(Math.max(loadedLen - 1, 0)).fill(""),
        "Sold Qty",
        ...Array(Math.max(soldLen - 1, 0)).fill(""),
        "Balance Qty",
        ...Array(Math.max(balLen - 1, 0)).fill(""),
      ];

      const headerRow2 = [
        "",
        "",
        "",
        "",
        ...loadedNames,
        "Total",
        ...soldNames,
        "Total",
        ...balNames,
        "Total",
      ];

      const fullCols = headerRow2.length;
      const padTo = (arr) => {
        const out = Array.isArray(arr) ? [...arr] : [];
        while (out.length < fullCols) out.push("");
        return out.slice(0, fullCols);
      };

      // ---------------- DATA ROWS ----------------
      const dataRows = this.reportData.map((r) => padTo([
        r.fsm_name || "-",
        r.asm_name || "-",
        r.distributor_name || "-",
        this.vehicleNoOf(r.rep_id) || r.vehicle_no || "-",

        ...this.itemsLoaded.map((it) => Number(r["loaded_" + it.item_id]) || 0),
        Number(r.loaded_total) || 0,

        ...this.itemsSold.map((it) => Number(r["sold_" + it.item_id]) || 0),
        Number(r.sold_total) || 0,

        ...this.itemsBal.map((it) => Number(r["bal_" + it.item_id]) || 0),
        Number(r.bal_total) || 0,
      ]));

      // ---------------- TOTAL ROW ----------------
      const totalRow = padTo([
        "TOTAL",
        "",
        "",
        "",
        ...this.itemsLoaded.map((it) => this.columnTotal("loaded_", it.item_id)),
        this.sumField("loaded_total"),
        ...this.itemsSold.map((it) => this.columnTotal("sold_", it.item_id)),
        this.sumField("sold_total"),
        ...this.itemsBal.map((it) => this.columnTotal("bal_", it.item_id)),
        this.sumField("bal_total"),
      ]);

      // ---------------- BUILD AOA ----------------
      const aoa = [
        ...filterRows,
        padTo(headerRow1),
        padTo(headerRow2),
        ...dataRows,
        totalRow,
      ];

      // ---------------- NO-DISPATCH ROWS ----------------
      if (this.showNoDispatchRows) {
        aoa.push(Array(fullCols).fill("")); // spacer
        aoa.push(padTo([`NO DISPATCH DISTRIBUTORS (${this.noDispatchRows.length})`]));

        const ndRows = this.noDispatchRows.map((r) => padTo([
          r.fsm_name || "-",
          r.asm_name || "-",
          r.distributor_name || "-",
          "", // vehicle blank

          ...this.itemsLoaded.map(() => 0),
          0,

          ...this.itemsSold.map(() => 0),
          0,

          ...this.itemsBal.map(() => 0),
          0,
        ]));

        aoa.push(...ndRows);
      }

      // ---------------- GENERATED INFO ----------------
      aoa.push(Array(fullCols).fill("")); 
      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const userFullName = (firstName + " " + lastName).trim() || "-";

      const now = new Date();
      const generatedAt = now.toLocaleString("en-US", { hour12: false });

      const generatedRows = [
        ["Generated by", userFullName],
        ["Generated at", generatedAt],
      ];

      generatedRows.forEach((r) => aoa.push(padTo(r)));

      // ---------------- MERGES ----------------
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      const startLoaded = 4;
      const startSold = startLoaded + loadedLen;
      const startBal = startSold + soldLen;

      ws["!merges"] = [
        { s: { r: startLoaded, c: 0 }, e: { r: startLoaded + 1, c: 0 } },
        { s: { r: startLoaded, c: 1 }, e: { r: startLoaded + 1, c: 1 } },
        { s: { r: startLoaded, c: 2 }, e: { r: startLoaded + 1, c: 2 } },
        { s: { r: startLoaded, c: 3 }, e: { r: startLoaded + 1, c: 3 } },

        { s: { r: startLoaded, c: startLoaded }, e: { r: startLoaded, c: startLoaded + loadedLen - 1 } },
        { s: { r: startLoaded, c: startSold }, e: { r: startLoaded, c: startSold + soldLen - 1 } },
        { s: { r: startLoaded, c: startBal }, e: { r: startLoaded, c: startBal + balLen - 1 } },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Daily_Monitoring");
      XLSX.writeFile(wb, `Daily_Monitoring_${this.selectedDate}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>




<style scoped>
html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: calc(100vh - 20px);
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
}

.dropdown-item {
  flex: 1;
  min-width: 180px;
  max-width: 250px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select,
input[type="date"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

select:focus,
input[type="date"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.search-container {
  display: flex;
  align-items: flex-end;
  justify-content: left;
}

.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-default:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}

.btn.button-default:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-export:hover {
  background-color: #218838;
  transform: scale(1.05);
}

.btn.button-export:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}

.card {
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.table th,
.table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
  background-color: #ffffff;
}

.table-striped tbody tr:nth-of-type(odd) td {
  background-color: #f8f9fa;
}

.table thead th {
  background-color: #f9f9f9;
  font-weight: bold;
  z-index: 3;
}

.table thead .header-row-1 th {
  position: sticky;
  top: 0;
  z-index: 5;
}

.table thead .header-row-2 th {
  position: sticky;
  top: 46px;
  z-index: 4;
}

.text-center {
  text-align: center;
}

.text-end {
  text-align: right;
}

.total-row td {
  background: #f1f3f5 !important;
  font-weight: 700;
}

/* ✅ highlight row */
.no-dispatch-row td {
  background: #fff3cd !important;
  font-weight: 700;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.table thead th.prod-head {
  text-align: center;
  vertical-align: middle;
  min-width: 120px;
  max-width: 160px;
  white-space: normal !important;
}

.prod-head-lines {
  line-height: 1.05;
}

.prod-head-lines>div {
  white-space: nowrap;
}

.section-end {
  border-right: 3px solid #e9ecef !important;
  padding-right: 9px !important;
}

.section-start {
  padding-left: 9px !important;
}

.group-end {
  border-right: 3px solid #e9ecef !important;
}
</style>
